import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/components/ui/card";
import { cn } from "@/ui/lib/utils";
import {
  Boxes,
  Truck,
  HardHat,
  Building2,
  Mountain,
  TriangleAlert,
  Cloud,
  Route,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetObjectPresenceTaxonomy } from "@/ml-exports/api/queries/taxonomy.queries";
import type { ObjectPresenceGroup } from "@/ml-exports/types/taxonomy";
import { TaxonomyMemberTable } from "./taxonomy-member-table";
import { TaxonomyOtherLabels } from "./taxonomy-other-labels";
import { TaxonomyTabSkeleton } from "./taxonomy-tab-skeleton";
import { TaxonomyVersionBadges } from "./taxonomy-version-badges";

/* ──────────────────────────────────────────────────────────────────
 * ObjectPresenceTaxonomyTab — read-only reference for the v1
 * object-presence taxonomy. Simpler than classification: flat groups,
 * no pipeline groups, no shortcuts, no quality gate. Each group is
 * tinted with its own `color.badge_bg`; icons are a frontend-only map
 * keyed by group key, with a neutral fallback for unknown keys.
 * ────────────────────────────────────────────────────────────────── */

const GROUP_ICONS: Record<string, React.ComponentType<{ className?: string }>> =
  {
    equipment: Truck,
    machinery: Truck,
    vehicles: Route,
    personnel: HardHat,
    people: HardHat,
    infrastructure: Building2,
    material: Mountain,
    materials: Mountain,
    hazard: TriangleAlert,
    hazards: TriangleAlert,
    environment: Cloud,
    conditions: Cloud,
  };

/** Display only — `group.selection` stays the raw wire value everywhere else. */
const SELECTION_LABEL_KEYS: Record<
  string,
  "objectPresence.selectionMultiple" | "objectPresence.selectionSingle"
> = {
  multiple: "objectPresence.selectionMultiple",
  single: "objectPresence.selectionSingle",
};

const ObjectPresenceGroupCard = ({ group }: { group: ObjectPresenceGroup }) => {
  const { t } = useTranslation("settings");
  const Icon = GROUP_ICONS[group.key] ?? Boxes;
  const accent = group.color?.badge_bg;
  const members = group.members ?? [];
  const count = group.count ?? members.length;
  const selectionKey = group.selection
    ? SELECTION_LABEL_KEYS[group.selection]
    : undefined;
  const selection = selectionKey
    ? t(selectionKey)
    : (group.selection ?? null);

  return (
    <Card
      className={cn("border-l-2", !accent && "border-l-zinc-500/50")}
      style={accent ? { borderLeftColor: accent } : undefined}
    >
      <CardHeader className="relative pb-3">
        {accent && (
          <span
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{ backgroundColor: accent }}
          />
        )}
        <div className="relative flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Icon
                className="h-4 w-4 shrink-0"
                {...(accent ? { style: { color: accent } } : {})}
              />
              {group.display_name}
            </CardTitle>
            <CardDescription className="font-mono text-[11px]">
              {t("objectPresence.groupKey", { key: group.key })}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {selection && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded border bg-muted/40 text-[10px] font-mono">
                {selection}
              </span>
            )}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded border bg-muted/40 text-[10px] font-mono">
              {t("objectPresence.labelCount", { count })}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <TaxonomyMemberTable
          members={members}
          columns={["color", "description"]}
        />
      </CardContent>
    </Card>
  );
};

export const ObjectPresenceTaxonomyTab = () => {
  const { t } = useTranslation("settings");
  const { data: taxonomy, isLoading } = useGetObjectPresenceTaxonomy();

  if (isLoading || !taxonomy) return <TaxonomyTabSkeleton />;

  const groups = taxonomy.groups ?? [];

  return (
    <div className="space-y-6">
      <TaxonomyVersionBadges
        schemaVersion={taxonomy.schema_version}
        version={taxonomy.version}
      />

      <div>
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <Boxes className="h-4 w-4 text-muted-foreground" />
          {t("objectPresence.groupsTitle")}
          <span className="text-[11px] font-normal text-muted-foreground">
            · {t("objectPresence.groupCount", { count: groups.length })}
          </span>
        </h2>
        {groups.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("objectPresence.empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {groups.map((group) => (
              <ObjectPresenceGroupCard key={group.key} group={group} />
            ))}
          </div>
        )}
      </div>

      <TaxonomyOtherLabels labels={taxonomy.other_labels} />
    </div>
  );
};

export default ObjectPresenceTaxonomyTab;
